<template>
    <button
        :type="type"
        :disabled="isBusy || disable"
        :class="classList"
        v-on="inputListeners"
    >
        <C1Spinner
            v-if="isBusy"
            class="mr-2"
        />

        <slot />
    </button>
</template>

<script>
    import { VARIANTS } from './utils/variants';
    import C1Spinner from './C1Spinner';

    export default {
        name: 'C1Button',

        components: {
            C1Spinner,
        },

        props: {
            type: {
                type: String,
                default: 'button',
            },

            variant: {
                type: String,
                default: null,
                validator: value => VARIANTS.includes(value),
            },

            size: {
                type: String,
                default: null,
                validator: value => ['sm', 'lg', 'naked', 'md', 'xl'].includes(value),
            },

            block: {
                type: Boolean,
                default: false,
            },

            isBusy: {
                type: Boolean,
                default: false,
            },

            disable: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {};
        },

        computed: {
            classList() {
                return [
                    this.getDefaultClass,
                    this.getTextColorClass,
                    this.getTextSizeClass,
                    this.getBorderColorClass,
                    this.getBgColorClass,
                    this.getPaddingClass,
                    this.getBlockClass,
                    this.getDisabledClass,
                ];
            },

            getDefaultClass() {
                let output = ['af-btn afu-cursor-pointer'];
                if (this.isOutline) {
                    output.push(`af-btn-${this.variant}`);
                } else if (this.localVariant) {
                    output.push(`af-btn-${this.localVariant}`);
                } else {
                    output.push('af-btn-default');
                }

                if (this.size) {
                    output.push(`af-btn-${this.size}`);
                }

                output.push(
                    'afu-text-center afu-inline-flex afu-items-center afu-justify-center afu-border-btnWidth afu-border-solid focus:afu-outline-none afu-rounded-btn afu-transition afu-ease-in-out afu-duration-150',
                );

                return output.join(' ');
            },

            getTextColorClass() {
                if (this.isOutline) {
                    return `afu-text-${this.localVariant} hover:afu-text-${this.localVariant}-dark focus:afu-text-${this.localVariant}-dark`;
                }

                if (!this.localVariant) {
                    return 'afu-text-neutral-light hover:afu-text-neutral-light focus:afu-text-neutral-light';
                }

                if (
                    this.localVariant == 'neutral' ||
                    this.localVariant == 'neutral-light'
                ) {
                    return 'afu-text-secondary hover:afu-text-secondary-dark focus:afu-text-secondary-dark';
                }

                return 'afu-text-neutral-light hover:afu-text-neutral-light focus:afu-text-neutral-light';
            },

            getTextSizeClass() {
                const sizes = {
                    default: 'af-text--xl afu-font-semibold',
                    md: 'af-text--xl afu-font-semibold',
                    sm: 'af-text--sm afu-font-semibold',
                    lg: 'af-text--4xl afu-font-semibold',
                };

                if (this.variant == 'neutral') {
                    return (
                        (sizes[this.size] || sizes.default) + ' afu-font-semibold'
                    );
                }

                if (this.variant == 'neutral-light') {
                    return (sizes[this.size] || sizes.default) + ' afu-font-normal';
                }

                return sizes[this.size] || sizes.default;
            },

            getBorderColorClass() {
                let output = [];
                if (this.isOutline) {
                    output.push(
                        `afu-border-${this.localVariant} hover:afu-border-neutral-dark focus:afu-border-neutral-dark`,
                    );
                } else if (this.localVariant) {
                    output.push(
                        `afu-border-${this.localVariant} hover:afu-border-${this.localVariant}-dark focus:afu-border-${this.localVariant}-dark`,
                    );
                } else if (!this.localVariant) {
                    output.push(
                        'afu-border-primary hover:afu-border-primary-dark focus:afu-border-primary-dark',
                    );
                } else {
                    output.push('afu-border-transparent');
                }

                return output.join(' ');
            },

            getBgColorClass() {
                if (!this.localVariant || this.isOutline) {
                    return 'afu-bg-transparent active:afu-bg-neutral-light focus:afu-bg-neutral-light hover:afu-bg-neutral-light';
                }

                if (this.localVariant == 'neutral') {
                    return 'afu-bg-transparent active:afu-bg-neutral-light focus:afu-bg-neutral-light hover:afu-bg-neutral-light';
                }

                return `afu-bg-${this.localVariant} active:afu-bg-${
                    this.localVariant
                }-dark focus:afu-bg-${this.localVariant}-dark ${
                    !this.isBusy ? `hover:afu-bg-${this.localVariant}-dark` : ''
                } `;
            },

            getPaddingClass() {
                const sizes = {
                    default: 'afu-p-btn',
                    sm: 'afu-p-btn-sm afu-w-btn-sm',
                    lg: 'afu-p-btn-lg',
                };

                return sizes[this.size] || sizes.default;
            },

            getBlockClass() {
                return this.block ? 'afu-w-full afu-justify-center' : '';
            },

            getDisabledClass() {
                return this.isBusy || this.disable ? 'afu-opacity-25' : '';
            },

            inputListeners() {
                return this.$listeners;
            },

            isOutline() {
                return this.variant ? this.variant.startsWith('outline') : false;
            },

            localVariant() {
                return this.variant ? this.variant.replace('outline-', '') : null;
            },
        },
    };
</script>
